import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MessageSquare, X, Send, Shield, User, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BUSINESS_NAME, SERVICES } from '@/src/constants';
import { Link } from 'react-router-dom';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const SYSTEM_PROMPT = `
You are a senior cybersecurity consultant at ${BUSINESS_NAME}. Your goal is to help visitors understand their security risks and guide them toward a professional consultation.

Follow this conversation flow:
1. GREETING: Start with a professional, authoritative greeting. Mention AGRANDINTL's expertise in enterprise-grade security.
2. QUALIFICATION: Ask about their business size, industry, and current security challenges (e.g., data breaches, compliance, ransomware).
3. RECOMMENDATION: Based on their input, suggest specific ${BUSINESS_NAME} services:
   - Vulnerability Assessment & Penetration Testing
   - SOC Monitoring & Threat Detection
   - Cloud & DevOps Security
   - Compliance & Risk Management (PCI DSS, SOC2, HIPAA)
   - Incident Response & Recovery
4. BOOKING CTA: Strongly encourage them to book a free consultation at the /contact page.

Tone: Professional, expert, authoritative, and proactive.
Expertise: You have deep knowledge of global threat landscapes, financial risks of breaches, and regulatory compliance.

If asked general cybersecurity questions, answer them accurately and then pivot back to how ${BUSINESS_NAME} can help.
Always keep the goal in mind: getting the user to book a consultation.
`;

export default function ChatAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { 
      role: 'assistant', 
      content: `Welcome to ${BUSINESS_NAME}. I'm your senior security consultant. How can I help you secure your enterprise today?` 
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: messages.map(m => ({
            role: m.role === 'assistant' ? 'model' : 'user',
            parts: [{ text: m.content }]
          })),
          input
        }),
      });

      if (!response.ok) throw new Error('Chat failed');
      const data = await response.json();

      const assistantMessage: Message = { 
        role: 'assistant', 
        content: data.text || "I apologize, I'm having trouble connecting to our intelligence network. Please try again or contact us directly." 
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chat Error:", error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I encountered a technical issue. For urgent security matters, please visit our contact page." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-8 right-8 z-50 flex flex-col items-end">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="mb-6 w-[400px] max-w-[calc(100vw-4rem)] h-[600px] max-h-[calc(100vh-12rem)] glass-card rounded-[2rem] border-accent/20 flex flex-col overflow-hidden shadow-2xl"
          >
            {/* Header */}
            <div className="p-6 border-b border-white/10 bg-accent/5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-background shadow-[0_0_15px_rgba(0,150,255,0.3)]">
                  <Shield className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-sm">Security Consultant</h3>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">Secure Line Active</span>
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="rounded-full hover:bg-white/5">
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Messages */}
            <div 
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-hide"
            >
              {messages.map((msg, i) => (
                <motion.div
                  initial={{ opacity: 0, x: msg.role === 'assistant' ? -10 : 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  key={i}
                  className={`flex ${msg.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
                >
                  <div className={`flex gap-3 max-w-[85%] ${msg.role === 'assistant' ? 'flex-row' : 'flex-row-reverse'}`}>
                    <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${msg.role === 'assistant' ? 'bg-accent/10 text-accent' : 'bg-white/10 text-white/60'}`}>
                      {msg.role === 'assistant' ? <Shield className="w-4 h-4" /> : <User className="w-4 h-4" />}
                    </div>
                    <div className={`p-4 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'assistant' 
                        ? 'bg-white/5 border border-white/10 rounded-tl-none' 
                        : 'bg-accent text-background font-medium rounded-tr-none'
                    }`}>
                      {msg.content}
                      {msg.role === 'assistant' && msg.content.includes('/contact') && (
                        <div className="mt-4">
                          <Button asChild size="sm" variant="accent" className="h-8 px-4 rounded-lg text-[10px] font-bold">
                            <Link to="/contact">Book Consultation <ArrowRight className="ml-2 w-3 h-3" /></Link>
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="flex gap-3 max-w-[85%]">
                    <div className="w-8 h-8 rounded-lg bg-accent/10 text-accent flex items-center justify-center">
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                    <div className="p-4 rounded-2xl bg-white/5 border border-white/10 rounded-tl-none">
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent/40 animate-bounce" />
                        <div className="w-1.5 h-1.5 rounded-full bg-accent/40 animate-bounce [animation-delay:0.2s]" />
                        <div className="w-1.5 h-1.5 rounded-full bg-accent/40 animate-bounce [animation-delay:0.4s]" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-6 border-t border-white/10 bg-background/50 backdrop-blur-xl">
              <form 
                onSubmit={(e) => { e.preventDefault(); handleSend(); }}
                className="relative"
              >
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about your security posture..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl py-4 pl-6 pr-14 text-sm focus:outline-none focus:border-accent/50 transition-all placeholder:text-white/20"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-lg bg-accent text-background flex items-center justify-center hover:bg-accent/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
              <p className="mt-3 text-[9px] text-center text-muted-foreground uppercase tracking-widest font-bold">
                End-to-End Encrypted Intelligence
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="w-16 h-16 rounded-full bg-accent text-background flex items-center justify-center shadow-[0_0_30px_rgba(0,150,255,0.4)] hover:shadow-[0_0_50px_rgba(0,150,255,0.6)] transition-all duration-500 relative group"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: 90 }}
            >
              <X className="w-7 h-7" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ opacity: 0, rotate: 90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: -90 }}
            >
              <MessageSquare className="w-7 h-7" />
            </motion.div>
          )}
        </AnimatePresence>
        {!isOpen && (
          <div className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full border-2 border-background flex items-center justify-center">
            <div className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
          </div>
        )}
      </motion.button>
    </div>
  );
}
